import type { AbilityService } from '@efdigital/ll-addon-auth';
import { IfAbility } from '@efdigital/ll-addon-auth';
import type { Client } from '@efdigital/ll-addon-data';
import type { Cohort } from '@efdigital/ll-addon-data/types/xceed/cohort';
import { EditContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import LtiCreateResourceLinkModal from '@efdigital/ll-admin-v2/components/lti/create-resource-link-modal.gts';
import { EditLtiResourceLink } from '@efdigital/ll-admin-v2/components/lti/edit-lti-resource-link.gts';
import {
  buildDeleteResourceLink,
  buildListResourceLinksByCohort,
} from '@efdigital/ll-admin-v2/data/builders/lti/lti-resource-links';
import type { LtiResourceLink } from '@efdigital/ll-admin-v2/data/types/lti';
import { alertLtiDeleteError } from '@efdigital/ll-admin-v2/utils/lti/delete-errors';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { cell, resource, use } from 'ember-resources';

interface Signature {
  Args: {
    cohort: Cohort;
    client: Client | null;
  };
}

export default class EditCohortLtiLinks extends Component<Signature> {
  @service declare store: Store;
  @service declare ability: AbilityService;

  @tracked resourceLinksRefresh = 0;
  @tracked private _deletingId: string | null = null;

  get canEditLinks(): boolean {
    return this.ability.canEdit('lti-links');
  }

  refreshLinks = () => {
    this.resourceLinksRefresh++;
  };

  @use cohortResourceLinks = resource(({ on }) => {
    const clientId = this.args.client?.id;
    const cohortId = this.args.cohort.id;
    const refresh = this.resourceLinksRefresh;
    void refresh;

    const state = cell<LtiResourceLink[]>([]);

    if (!clientId || !cohortId) {
      return () => state.current;
    }

    let cancelled = false;

    void this.store
      .request(buildListResourceLinksByCohort(cohortId, clientId))
      .then((result) => {
        if (!cancelled) {
          state.set(result.content.data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          state.set([]);
        }
      });

    on.cleanup(() => {
      cancelled = true;
    });

    return () => state.current;
  });

  get cohortLinks(): LtiResourceLink[] {
    return this.cohortResourceLinks ?? [];
  }

  isDeletingLink = (link: LtiResourceLink): boolean =>
    this._deletingId === link.id;

  isLinkDeleteDisabled = (link: LtiResourceLink, isEditing: boolean): boolean =>
    isEditing || this.isDeletingLink(link);

  deleteResourceLink = async (link: LtiResourceLink) => {
    const platformId = link.platformId;
    const consumedToolId = link.consumedToolId;
    const deploymentId = link.consumedToolDeploymentId;

    if (!platformId || !consumedToolId || !deploymentId || !link.id) {
      return;
    }

    const label = link.platformResourceLinkId;
    if (
      !confirm(
        `Are you sure you want to delete resource link ${label}? Learners launching with this claim will no longer be enrolled in this cohort.`
      )
    ) {
      return;
    }

    this._deletingId = link.id;
    try {
      await this.store.request(
        buildDeleteResourceLink(link, platformId, consumedToolId, deploymentId)
      );
      this.store.unloadRecord(link);
      this.refreshLinks();
    } catch (error) {
      alertLtiDeleteError(error);
    } finally {
      this._deletingId = null;
    }
  };

  <template>
    <h3 class="gud-eyebrow">LTI integration</h3>
    <p>
      Resource links map an LMS launch claim value to this cohort. When a
      learner launches from the LMS for the first time, just-in-time (JIT)
      enrolment adds them to the list of participants in this cohort
      automatically. You will not need to manually add users to the list of
      participants.
    </p>

    {{#unless @client}}
      <div class="alert alert-warning">
        This cohort has no client — LTI links cannot be configured.
      </div>
    {{/unless}}

    {{#if @client.id}}
      {{#if @cohort.id}}
        <h4 class="h6">Resource links for this cohort</h4>

        {{#if this.cohortLinks.length}}
          <EditContext as |context|>
            {{#each this.cohortLinks as |link|}}
              <div class="card shadow-sm mb-4" data-test-lti-cohort-link-card>
                <div
                  class="card-header bg-transparent d-flex justify-content-between align-items-center"
                >
                  <span class="fw-semibold font-monospace">
                    Resource Link
                  </span>
                  <IfAbility @ability="lti-links" @atLeast="editor">
                    <:if>
                      <button
                        type="button"
                        class="btn btn-sm btn-outline-danger"
                        disabled={{this.isLinkDeleteDisabled
                          link
                          context.isEditing
                        }}
                        data-test-lti-delete-resource-link
                        {{on "click" (fn this.deleteResourceLink link)}}
                      >
                        {{#if (this.isDeletingLink link)}}
                          Deleting…
                        {{else}}
                          <iconify-icon inline icon="ph:trash" />
                          <span class="visually-hidden">Delete resource link</span>
                        {{/if}}
                      </button>
                    </:if>
                  </IfAbility>
                </div>
                <div class="card-body">
                  <EditLtiResourceLink @item={{link}} @context={{context}} />
                </div>
              </div>
            {{/each}}

            <IfAbility @ability="lti-links" @atLeast="editor">
              <:if>
                <LtiCreateResourceLinkModal
                  @clientId={{@client.id}}
                  @cohortId={{@cohort.id}}
                  @onCreated={{this.refreshLinks}}
                >
                  <:trigger as |openModal|>
                    <button
                      type="button"
                      class="btn btn-primary"
                      data-test-lti-create-resource-link
                      {{on "click" openModal}}
                    >
                      <iconify-icon inline icon="ph:plus" />
                      Create resource link
                    </button>
                  </:trigger>
                </LtiCreateResourceLinkModal>
              </:if>
            </IfAbility>
          </EditContext>
        {{else}}
          <p class="text-muted" data-test-lti-cohort-links-empty>
            No resource links mapped to this cohort yet.
          </p>

          <IfAbility @ability="lti-links" @atLeast="editor">
            <:if>
              <LtiCreateResourceLinkModal
                @clientId={{@client.id}}
                @cohortId={{@cohort.id}}
                @onCreated={{this.refreshLinks}}
              >
                <:trigger as |openModal|>
                  <button
                    type="button"
                    class="btn btn-primary"
                    data-test-lti-create-resource-link
                    {{on "click" openModal}}
                  >
                    <iconify-icon inline icon="ph:plus" />
                    Create resource link
                  </button>
                </:trigger>
              </LtiCreateResourceLinkModal>
            </:if>
          </IfAbility>
        {{/if}}
      {{/if}}
    {{/if}}
  </template>
}
